import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import ClaimDetails from './ClaimDetails';

// ─── Styled Components ───────────────────────────────────────────────────────

const PageWrapper = styled.div`
  background: #f9fafb;
  padding: 1.5rem;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 2rem 2.5rem;
  }
`;

const Card = styled.div`
  margin: 0 auto;
  max-width: 1280px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06);
`;

const TabsContainer = styled.div`
  border-bottom: 1px solid #e5e7eb;
`;

const TabsList = styled.div`
  display: flex;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const TabButton = styled.button`
  flex: 1 1 auto;
  min-width: 100px;
  padding: 1rem 1.5rem;
  font-weight: 500;
  color: ${p => p.active ? '#1d4ed8' : '#4b5563'};
  border-bottom: 3px solid ${p => p.active ? '#2563eb' : 'transparent'};
  background: none;
  transition: all 0.2s;

  &:hover {
    color: ${p => p.active ? '#1d4ed8' : '#111827'};
  }
`;

const ContentArea = styled.div`
  padding: 1.75rem;
`;

const SectionTitle = styled.h3`
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const Badge = styled.span`
  border-radius: 9999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

const PendingBadge = styled(Badge)`
  background: #fffbeb;
  color: #b45309;
`;

const ProvidedBadge = styled(Badge)`
  background: #ecfdf5;
  color: #065f46;
`;

const ActionCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
  background: white;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: 500;

  ${p => {
    switch (p.status) {
      case 'Pending':    return 'background:#fffbeb; color:#b45309;';
      case 'Completed':  return 'background:#ecfdf5; color:#065f46;';
      case 'In Progress':return 'background:#eff6ff; color:#1d4ed8;';
      case 'Approved':   return 'background:#f0fdf4; color:#166534;';
      case 'Rejected':   return 'background:#fef2f2; color:#991b1b;';
      default:           return 'background:#f3f4f6; color:#374151;';
    }
  }}
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ApproveButton = styled.button`
  flex: 1;
  padding: 0.625rem;
  border-radius: 6px;
  background: #16a34a;
  color: white;
  font-weight: 500;
  transition: background 0.2s;

  &:hover:not(:disabled) { background: #15803d; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RejectButton = styled(ApproveButton)`
  background: #dc2626;
  &:hover:not(:disabled) { background: #b91c1c; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
  }
`;

// ─── Carousel ────────────────────────────────────────────────────────────────

const CarouselWrapper = styled.div`
  position: relative;
`;

const CarouselTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: 1rem;
  padding-bottom: 0.75rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const CarouselSlide = styled.div`
  flex: 0 0 85%;
  scroll-snap-align: start;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);

  @media (min-width: 640px)  { flex: 0 0 65%; }
  @media (min-width: 1024px) { flex: 0 0 45%; }

  img {
    width: 100%;
    aspect-ratio: 4 / 3;
    object-fit: cover;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const Dot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.active ? '#2563eb' : '#d1d5db'};
  border: none;
  cursor: pointer;
  transition: all 0.2s;
`;

// ─── Other Components ────────────────────────────────────────────────────────

const TrackItem = styled.div`
  display: flex;
  gap: 1.25rem;
`;

const IconCircle = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  background: ${p => p.completed ? '#2563eb' : '#e5e7eb'};
  color: ${p => p.completed ? 'white' : '#6b7280'};
`;

const Line = styled.div`
  width: 2px;
  flex: 1;
  margin: 0.25rem 0 0.25rem 21px;
  background: ${p => p.completed ? '#2563eb' : '#e5e7eb'};
`;

const NotificationCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

// ─── Main Component ──────────────────────────────────────────────────────────

function Claim() {
  const [activeTab, setActiveTab] = useState('actions');
  const carouselRef = useRef(null);

  const [pendingFields, setPendingFields] = useState([
    { key: 'incidentDate', label: 'Date of Incident', type: 'date', value: '', required: true },
    { key: 'location',     label: 'Location',        type: 'text', value: '', required: true },
    { key: 'visibility',   label: 'Visibility',      type: 'select', value: '', options: ['Day', 'Night', 'Dusk/Dawn'], required: false },
    { key: 'passengers',   label: 'No. of Passengers', type: 'number', value: '', required: false },
  ]);

  const providedFields = [
    { key: 'claim_form', label: 'Claim Form', value: 'Uploaded – Jan 10, 2026' },
    { key: 'police_ref', label: 'Police Reference No.', value: 'OB 45/321/2026' },
  ];

  const claimImages = [
    'https://placehold.co/800x600/3B82F6/white/png?text=Front+View',
    'https://placehold.co/800x600/EF4444/white/png?text=Right+Side+Damage',
    'https://placehold.co/800x600/10B981/white/png?text=Rear+View',
    'https://placehold.co/800x600/F59E0B/white/png?text=Interior+Damage',
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const tabs = [
    { id: 'actions',       label: 'Actions' },
    { id: 'form',          label: 'Claim Form' },
    { id: 'track',         label: 'Progress' },
    { id: 'notifications', label: 'Notifications' },
  ];

  const trackSteps = [
    { title: 'Claim Submitted',    description: 'Received and logged',               date: 'Jan 10, 2026', completed: true,  icon: 'fa-check' },
    { title: 'Under Review',       description: 'Initial document verification',     date: 'Jan 11, 2026', completed: true,  icon: 'fa-file-alt' },
    { title: 'Assessment',         description: 'Physical inspection & valuation',   date: 'In Progress',  completed: false, icon: 'fa-search' },
    { title: 'Decision',           description: 'Final approval / rejection',        date: 'Pending',      completed: false, icon: 'fa-gavel' },
    { title: 'Payment Issued',     description: 'Funds transferred',                 date: 'Pending',      completed: false, icon: 'fa-money-bill-wave' },
  ];

  const handleScrollToImage = (index) => {
    if (!carouselRef.current) return;
    const slideWidth = carouselRef.current.scrollWidth / claimImages.length;
    carouselRef.current.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
    setCurrentImageIndex(index);
  };

  return (
    <PageWrapper>
      <ClaimDetails />

      <Card>
        <TabsContainer>
          <TabsList>
            {tabs.map(tab => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabsList>
        </TabsContainer>

        <ContentArea>
          {/* ─── ACTIONS ─────────────────────────────────────────────── */}
          {activeTab === 'actions' && (
            <div style={{ display: 'grid', gap: '1.25rem', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
              {/* You can map real pending actions here */}
              <ActionCard>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>Satisfactory Note</div>
                <div style={{ fontWeight: 500, marginBottom: '1rem' }}>Photos quality acceptable</div>
                <div style={{ marginBottom: '1rem' }}>
                  <StatusBadge status="Pending">Pending</StatusBadge>
                </div>
                <ButtonRow>
                  <RejectButton>Reject</RejectButton>
                  <ApproveButton>Approve</ApproveButton>
                </ButtonRow>
              </ActionCard>
              {/* More action cards... */}
            </div>
          )}

          {/* ─── CLAIM FORM ───────────────────────────────────────────── */}
          {activeTab === 'form' && (
            <div style={{ display: 'grid', gap: '3rem' }}>
              {/* Pending */}
              <div>
                <SectionTitle>
                  Pending Information
                  <PendingBadge>{pendingFields.filter(f => !f.value && f.required).length}</PendingBadge>
                </SectionTitle>

                <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {pendingFields.map(field => (
                    <div key={field.key}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                        {field.label}
                        {field.required && <span style={{ color: '#ef4444' }}> *</span>}
                      </label>
                      {field.type === 'select' ? (
                        <Select
                          value={field.value}
                          onChange={e => setPendingFields(prev =>
                            prev.map(f => f.key === field.key ? { ...f, value: e.target.value } : f)
                          )}
                        >
                          <option value="">Select...</option>
                          {field.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </Select>
                      ) : (
                        <Input
                          type={field.type}
                          value={field.value}
                          onChange={e => setPendingFields(prev =>
                            prev.map(f => f.key === field.key ? { ...f, value: e.target.value } : f)
                          )}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Provided */}
              <div>
                <SectionTitle>
                  Provided Information
                  <ProvidedBadge>{providedFields.length}</ProvidedBadge>
                </SectionTitle>

                <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                  {providedFields.map(f => (
                    <div key={f.key}>
                      <div style={{ fontWeight: 500, marginBottom: '0.4rem' }}>{f.label}</div>
                      <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                        {f.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Images */}
              {claimImages.length > 0 && (
                <div>
                  <SectionTitle>Supporting Images ({claimImages.length})</SectionTitle>
                  <CarouselWrapper>
                    <CarouselTrack ref={carouselRef}>
                      {claimImages.map((src, i) => (
                        <CarouselSlide key={i}>
                          <img src={src} alt={`Claim image ${i + 1}`} />
                        </CarouselSlide>
                      ))}
                    </CarouselTrack>

                    <CarouselDots>
                      {claimImages.map((_, i) => (
                        <Dot
                          key={i}
                          active={i === currentImageIndex}
                          onClick={() => handleScrollToImage(i)}
                        />
                      ))}
                    </CarouselDots>
                  </CarouselWrapper>
                </div>
              )}

              {/* Documents */}
              <div>
                <SectionTitle>Documents</SectionTitle>
                <div style={{ display: 'grid', gap: '0.75rem' }}>
                  <DocumentItem>
                    <DocumentInfo>
                      <i className="fa fa-file-pdf fa-2x" style={{ color: '#dc2626' }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>Driving Licence.pdf</div>
                        <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>2.4 MB • Jan 9, 2026</div>
                      </div>
                    </DocumentInfo>
                    <button style={{ padding: '0.5rem 1rem', color: '#2563eb', fontWeight: 500 }}>
                      Download
                    </button>
                  </DocumentItem>

                  <DocumentItem>
                    <DocumentInfo>
                      <i className="fa fa-file-pdf fa-2x" style={{ color: '#dc2626' }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>Police Abstract.pdf</div>
                        <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>1.8 MB • Jan 10, 2026</div>
                      </div>
                    </DocumentInfo>
                    <button style={{ padding: '0.5rem 1rem', color: '#2563eb', fontWeight: 500 }}>
                      Download
                    </button>
                  </DocumentItem>
                </div>
              </div>
            </div>
          )}

          {/* ─── PROGRESS / TRACK ─────────────────────────────────────── */}
          {activeTab === 'track' && (
            <div style={{ display: 'grid', gap: '1.75rem' }}>
              {trackSteps.map((step, i) => (
                <TrackItem key={i}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <IconCircle completed={step.completed}>
                      <i className={`fa ${step.icon}`} />
                    </IconCircle>
                    {i < trackSteps.length - 1 && <Line completed={step.completed} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{step.title}</h4>
                    <p style={{ color: '#4b5563', fontSize: '0.9375rem' }}>{step.description}</p>
                    <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                      {step.date}
                    </p>
                  </div>
                </TrackItem>
              ))}
            </div>
          )}

          {/* ─── NOTIFICATIONS ────────────────────────────────────────── */}
          {activeTab === 'notifications' && (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {/* Add your notifications here */}
              <NotificationCard>
                <IconWrapper><i className="fa fa-check-circle" /></IconWrapper>
                <div>
                  <h4 style={{ fontWeight: 600 }}>Claim Submitted</h4>
                  <p style={{ marginTop: '0.25rem', color: '#4b5563' }}>
                    Your claim #CLM-2026-001 has been received and is under review.
                  </p>
                  <p style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: '#6b7280' }}>2 hours ago</p>
                </div>
              </NotificationCard>
            </div>
          )}
        </ContentArea>
      </Card>
    </PageWrapper>
  );
}

export default Claim;